import PropTypes from 'prop-types';
import React from 'react';
import { Statistics, Numbers, Tooltip, FormatDate } from '@weezevent/nacre';
import i18n from 'i18next';

import { useTranslation } from 'react-i18next';
import { Loader } from 'semantic-ui-react';

import Styles from '../../../styles/event/dashboard/eventDashboard.module.css';
import { StatisticsMoreLabel } from './statisticsAttendees';

export function StatisticsNextPayout({ statistics = null }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const labelNextPayout = React.useMemo(() => {
        if (+statistics?.next_payment > 0 && statistics?.payment_period === 'bimonthly' && statistics?.next_payment_date) {
            return (
                <span>
                    {t('ticket.eventDashboard.statisticsPayments.nextPayment')} <FormatDate date={statistics?.next_payment_date} language={locale} format={'date-numeric'} />
                </span>
            );
        }

        if (+statistics?.next_payment > 0 && statistics?.payment_period === 'end_of_event') {
            return t('ticket.eventDashboard.statisticsPayments.paymentEndEvent');
        }

        return t('ticket.eventDashboard.statisticsPayments.noPendingPayment');
    }, [statistics]);

    if (!statistics) {
        return null;
    }

    return <div className={Styles['statistics-payments-next-payout']}>{labelNextPayout}</div>;
}

StatisticsNextPayout.propTypes = {
    statistics: PropTypes.object
};

export default function StatisticsPayments({ data = null, currency = 'EUR', titleKey = 'next_payment', prefixUrl, organization }) {
    const { t } = useTranslation();
    const locale = i18n.language;

    if (organization.isFranchise()) {
        return null;
    }

    const statistics = React.useMemo(() => {
        if (!data) {
            return null;
        }

        let tmpAttendeesStats = data.find(groupStats => groupStats.name === 'attendees_stats').value;
        let tmpRevenuesStats = data.find(groupStats => groupStats.name === 'revenues_stats').value;
        let tmpPaymentsStats = data.find(groupStats => groupStats.name === 'payments_stats').value;

        return { ...tmpAttendeesStats, ...tmpPaymentsStats, sum_revenue: tmpRevenuesStats.sum_revenue };
    }, [data]);

    if (!statistics) {
        return (
            <Statistics stat="">
                <Loader active inline="centered" />
            </Statistics>
        );
    }

    const rows = [
        {
            title: <Statistics.CellStyle grey={+statistics.sum_revenue === 0}>{t('ticket.eventDashboard.statisticsPayments.sumPaymentTransfered')}</Statistics.CellStyle>,
            value: (
                <Statistics.CellStyle bold grey={+statistics.sum_revenue === 0}>
                    <Numbers value={statistics.sum_payment_transfered} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        },
        {
            title: (
                <Statistics.CellStyle grey={+statistics.sum_revenue === 0}>
                    <Tooltip
                        margin={false}
                        position={'bottom'}
                        triggerOnHover
                        title={
                            <span className={Styles['statistics-tooltips']}>
                                {t('ticket.eventDashboard.statisticsPayments.collectedWeezCommissionHelp')}{' '}
                                <Numbers value={statistics.collected_weez_commission || 0} locale={locale} currency={currency} showNullDecimal />
                            </span>
                        }
                    >
                        <span> {t('ticket.eventDashboard.statisticsPayments.collectedWeez')} </span>
                    </Tooltip>
                </Statistics.CellStyle>
            ),
            value: (
                <Statistics.CellStyle bold grey={+statistics.sum_revenue === 0}>
                    <Numbers value={statistics.collected_weez} locale={locale} currency={currency} showNullDecimal />
                </Statistics.CellStyle>
            )
        }
    ];

    return (
        <Statistics
            className={+statistics.collected_weez > 0 ? Styles['statistics-payments'] : Styles['statistics-payments-disable']}
            helpLabel={
                <div className={Styles['statistics-payment-tooltip']}>
                    {t('ticket.eventDashboard.statisticsPayments.subtitleHelp')}
                    <br />
                    {t('ticket.eventDashboard.statisticsPayments.collectedWeezCommissionHelp')}{' '}
                    <Numbers value={statistics.collected_weez_commission || 0} locale={locale} currency={currency} showNullDecimal />
                </div>
            }
            subtitle={t('ticket.eventDashboard.statisticsPayments.subtitle')}
            stat={<Numbers value={statistics[titleKey]} locale={locale} currency={currency} showNullDecimal />}
            moreURL={`${prefixUrl}/finance/transfer_history`}
            moreLabel={
                +statistics.total_attendees > 0 && +statistics.collected_weez > 0 ? <StatisticsMoreLabel title={t('ticket.eventDashboard.statisticsPayments.link')} /> : null
            }
        >
            {+statistics.total_attendees > 0 && +statistics.collected_weez > 0 && (
                <Statistics.Content>
                    <StatisticsNextPayout statistics={statistics} />
                    <Statistics.Table data={rows} />
                </Statistics.Content>
            )}
        </Statistics>
    );
}

StatisticsPayments.propTypes = {
    data: PropTypes.array,
    currency: PropTypes.string,
    titleKey: PropTypes.string,
    prefixUrl: PropTypes.string.isRequired
};
